/**
 * Channel Console page (A7Y-3776).
 *
 * A self-sustaining bird's-eye view of marketing channel performance for the
 * CMO. This ticket ships the Website module; the Product (A7Y-3778) and
 * LinkedIn (A7Y-3779) modules mount below it, and the reliability shell
 * (A7Y-3777) adds the nightly pull, stale flag and Slack alert.
 *
 * The page does not poll — see `channel-console/fetch-policy.ts` for why that
 * matters more here than on Audit Spend. The Refresh button runs an on-demand
 * pull (`POST /website/refresh`, the reliability shell's shared core,
 * A7Y-3777) rather than re-reading a cache, because a refresh control sitting
 * in front of data it cannot actually pull fresh is an affordance that lies.
 *
 * A7Y-3929 added a bounded re-read loop here, driven by a `warming` list the
 * server returned while it aggregated days behind the response. A7Y-3777
 * retires it, and that is a removal rather than a regression: the GET path no
 * longer pulls anything, so there is nothing in flight for a re-read to
 * discover, and a timer that re-reads unchanging data IS the polling this
 * page refuses to do. What A7Y-3929 was protecting — a cold window that fills
 * without anyone clicking — is now the nightly pull's job, and the stale flag
 * says so out loud when it has not run.
 *
 * The "data from …" stamp is driven by the server's `generated_at`, not by
 * react-query's `dataUpdatedAt`: with a cached window those differ, and
 * showing fetch time over cached numbers is the precise failure a cache
 * invites.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { CHANNEL_CONSOLE_FETCH_POLICY } from './channel-console/fetch-policy';
import { WebsiteModule } from './channel-console/WebsiteModule';
import ProductModule from './channel-console/ProductModule';
import { LinkedInModule } from './channel-console/LinkedInModule';

const WEBSITE_QUERY_KEY = ['channel-console', 'website'];
const PRODUCT_QUERY_KEY = ['channel-console', 'product'];
const LINKEDIN_QUERY_KEY = ['channel-console', 'linkedin'];

function formatLocal(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function ChannelConsolePage() {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailures, setRefreshFailures] = useState<string[]>([]);

  const website = useQuery({
    queryKey: WEBSITE_QUERY_KEY,
    queryFn: () => api.getChannelConsoleWebsite(),
    ...CHANNEL_CONSOLE_FETCH_POLICY,
  });

  const product = useQuery({
    queryKey: PRODUCT_QUERY_KEY,
    queryFn: () => api.getChannelConsoleProduct(),
    ...CHANNEL_CONSOLE_FETCH_POLICY,
  });

  const linkedin = useQuery({
    queryKey: LINKEDIN_QUERY_KEY,
    queryFn: () => api.getChannelConsoleLinkedIn(),
    ...CHANNEL_CONSOLE_FETCH_POLICY,
  });

  async function refresh() {
    setRefreshing(true);
    // Collected, then rendered. A `.catch` that only calls `console.error`
    // makes a total upstream outage render byte-identically to a successful
    // refresh (Karen round 2, F1 — measured: `diffLen: 0` across the whole
    // page after rejecting all three). The stale flag cannot cover for it
    // either: that flag lives in the cached GET payload, and a failed POST
    // never updates it.
    const failures: string[] = [];
    const attempt = async (label: string, run: () => Promise<void>) => {
      try {
        await run();
      } catch (err) {
        failures.push(`${label}: ${err instanceof Error ? err.message : String(err)}`);
      }
    };

    try {
      // Runs the pull core on demand and writes the response straight into
      // the query cache — no separate refetch needed. It pulls at most three
      // missing days under a 20s budget (A7Y-3929's measured numbers), so on
      // a cold window the click closes part of it and the notice below says
      // how much is left rather than the button appearing to have done
      // nothing.
      //
      // D10 — sequential, not parallel: Mixpanel serializes exports per
      // project, so the pulls run one after another, each self-budgeted at
      // 20s. Each response is written into its own query cache as soon as it
      // lands, so an earlier section can render its refreshed data while a
      // later pull is still running.
      //
      // Each call is isolated (A7Y-3779). Before that they shared one `try`
      // with no `catch`, so the first rejection skipped every later refresh
      // and escaped the click handler. Isolation is what makes "one failing
      // upstream cannot abort the others" true; the banner below is what
      // makes the failure visible.
      await attempt('Website', async () => {
        queryClient.setQueryData(WEBSITE_QUERY_KEY, await api.refreshChannelConsoleWebsite());
      });
      await attempt('Product', async () => {
        queryClient.setQueryData(PRODUCT_QUERY_KEY, await api.refreshChannelConsoleProduct());
      });
      // LinkedIn hits AuthoredUp rather than Mixpanel, so it contends with
      // neither of the two above for an upstream lock; it is last in the
      // chain rather than concurrent only because the button reports one
      // combined busy state. Its pull is a single snapshot, not a day
      // window, so it is the cheapest of the three.
      await attempt('LinkedIn', async () => {
        queryClient.setQueryData(LINKEDIN_QUERY_KEY, await api.refreshChannelConsoleLinkedIn());
      });
    } finally {
      setRefreshFailures(failures);
      setRefreshing(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Channel Console"
        subtitle="Marketing channel performance — website, product, and LinkedIn."
      />

      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-zinc-500">Data from {formatLocal(website.data?.generated_at)}</p>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing || website.isFetching}
          className="inline-flex items-center gap-2 rounded-md border border-zinc-300 dark:border-zinc-600 px-3 py-1.5 text-sm text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Pull fresh now
        </button>
      </div>

      {refreshFailures.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 p-4">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {refreshFailures.length === 1
              ? '1 channel failed to refresh'
              : `${refreshFailures.length} channels failed to refresh`}
          </h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            The sections below still show their last good numbers. Each failure is listed with the
            message the upstream returned.
          </p>
          <ul className="mt-2 space-y-1">
            {refreshFailures.map((failure) => (
              <li key={failure} className="font-mono text-xs text-red-700 dark:text-red-400">
                {failure}
              </li>
            ))}
          </ul>
        </div>
      )}

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Website</h2>
        {website.isError ? (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/40 p-4 text-sm text-red-800 dark:text-red-200">
            Failed to load the website module.
          </div>
        ) : (
          <WebsiteModule data={website.data?.data} loading={website.isLoading} />
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Product</h2>
        <ProductModule
          data={product.data?.data}
          isLoading={product.isLoading}
          error={product.isError ? 'Failed to load the product module.' : null}
        />
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">LinkedIn</h2>
        <LinkedInModule
          data={linkedin.data?.data}
          loading={linkedin.isLoading}
          error={linkedin.isError ? 'Failed to load the LinkedIn module.' : null}
        />
      </section>
    </div>
  );
}
